import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CategoryIcon from '@mui/icons-material/Category';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ListPageLayout from '../components/shared/list-page-layout';
import { useUsefulLinksController } from '../useful-links/context/useful-links-controller';
import { useUsefulLinksState } from '../useful-links/context/useful-links-context';
import { IUsefulLinkResponse } from '../../model/base/response/useful-links-response';

const normalizeUrl = (url: string): string => {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) return url;
  return `https://${url}`;
};

export function CartaServicosComponent() {
  const { loadLinks } = useUsefulLinksController();
  const { links, loading, error } = useUsefulLinksState();
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

  useEffect(() => {
    loadLinks({ page: 0, pageSize: 100 });
  }, []);

  const handleAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  const groupedLinks = links.reduce<Record<string, IUsefulLinkResponse[]>>((acc, link) => {
    const cat = link.category?.trim() || 'Sem Categoria';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(link);
    return acc;
  }, {});

  const sortedCategories = Object.keys(groupedLinks).sort((a, b) =>
    a.localeCompare(b, 'pt-BR')
  );

  return (
    <ListPageLayout
      title="Carta de Serviços"
      subtitle="Serviços ao cidadão organizados por categoria"
      breadcrumbs={[{ label: 'Carta de Serviços' }]}
    >
      {/* Cabeçalho institucional */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AssignmentOutlinedIcon sx={{ fontSize: 36 }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              Carta de Serviços ao Cidadão
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.95, lineHeight: 1.7 }}>
            Os itens abaixo trazem alguns dos Serviços ao Cidadão, que vem para facilitar e
            ampliar o acesso da população aos serviços públicos, com as informações necessárias
            para que todos tenham suas necessidades atendidas.
          </Typography>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && links.length === 0 && (
        <Alert severity="info">
          Nenhum serviço cadastrado no momento.
        </Alert>
      )}

      {!loading && !error && sortedCategories.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <CategoryIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {sortedCategories.length}{' '}
              {sortedCategories.length === 1 ? 'categoria disponível' : 'categorias disponíveis'}
            </Typography>
          </Box>

          {sortedCategories.map((category, index) => {
            const categoryLinks = groupedLinks[category];
            const panelId = `panel-${index}`;

            return (
              <Accordion
                key={category}
                expanded={expandedPanel === panelId}
                onChange={handleAccordionChange(panelId)}
                elevation={2}
                sx={{
                  mb: 1.5,
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                  '&.Mui-expanded': {
                    mb: 1.5,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${panelId}-content`}
                  id={`${panelId}-header`}
                  sx={{
                    background: (theme) =>
                      expandedPanel === panelId
                        ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                        : theme.palette.background.paper,
                    color: expandedPanel === panelId ? 'white' : 'inherit',
                    borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
                    transition: 'all 0.3s ease',
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      color: expandedPanel === panelId ? 'white' : 'primary.main',
                    },
                    '&:hover': {
                      background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: 'white',
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: 'white',
                      },
                    },
                    minHeight: 64,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <CategoryIcon sx={{ fontSize: 22, flexShrink: 0 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                      {category}
                    </Typography>
                    <Chip
                      label={`${categoryLinks.length} ${categoryLinks.length === 1 ? 'serviço' : 'serviços'}`}
                      size="small"
                      sx={{
                        backgroundColor: expandedPanel === panelId
                          ? 'rgba(255,255,255,0.25)'
                          : 'primary.light',
                        color: expandedPanel === panelId ? 'white' : 'white',
                        fontWeight: 600,
                        mr: 1,
                      }}
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0, backgroundColor: '#fafafa' }}>
                  <List disablePadding>
                    {categoryLinks.map((link, linkIndex) => (
                      <ListItem
                        key={link.id}
                        disablePadding
                        divider={linkIndex < categoryLinks.length - 1}
                      >
                        <ListItemButton
                          component="a"
                          href={normalizeUrl(link.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            py: 1.5,
                            px: 3,
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              '& .link-name': { color: 'primary.main' },
                              '& .link-icon': { color: 'primary.main' },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <LinkIcon
                              className="link-icon"
                              fontSize="small"
                              sx={{ color: 'text.secondary', transition: 'color 0.2s' }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                className="link-name"
                                sx={{
                                  fontWeight: 500,
                                  color: 'text.primary',
                                  transition: 'color 0.2s',
                                }}
                              >
                                {link.name}
                              </Typography>
                            }
                          />
                          <OpenInNewIcon
                            fontSize="small"
                            sx={{ color: 'text.disabled', flexShrink: 0, ml: 1 }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </ListPageLayout>
  );
}
